// Your existing main API file structure with aiHelpers added

import axios from 'axios';
import baseVue from '@/main.js';
import ACCOUNT_ROUTES from './account.js';
import PATIENT_ROUTES from './patient.js';
import PROVIDER_ROUTES from './provider.js';
import DIAGNOSTIC_ROUTES from './diagnostic.js';
import APPOINTMENT_ROUTES from './appointment.js';
import FILE_ROUTES from './file.js';
import REPORT_ROUTES from './report.js';
import AI_ASSISTANT_ROUTES from './aiassistant.js'; 
import { aiHelpers } from './aiHelpers.js';
import SCHEDULER_ROUTES from './scheduler.js';
import PATIENT_BILLING_ROUTES from './patientbilling.js';

const MHI_API = {
    http: axios.create({
        baseURL: process.env.VUE_APP_API_URL,
    }),
    install: function (Vue) {
        var BASE_URL = '/';
        if (process.env.NODE_ENV === 'development')
            BASE_URL += 'api/';
        else
            BASE_URL += 'portal/api/';
        const CODE_OK = 200;
        const CODE_CREATED = 201;
        const CODE_NO_CONTENT = 204;
        const CODE_BAD_REQUEST = 400;
        const CODE_UNAUTHORIZED = 401;
        const CODE_FORBIDDEN = 403;
        const CODE_NOT_FOUND = 404;
        const CODE_INTERNAL_SERVER_ERROR = 500;

        this.http = axios.create({
            baseURL: BASE_URL,
        });
        this.http.interceptors.response.use(async response => {
            return Promise.resolve(response);
        }, async error => {
            if (error.response?.status == CODE_UNAUTHORIZED && baseVue.$route.path != '/') {
                baseVue.$window.location = "/";
            }
            return Promise.resolve(error.response ?? { status: CODE_INTERNAL_SERVER_ERROR });
        });

        const baseAPI = {
            http: this.http,
            load: async function (controller, route, params) {
                let response = null;

                // "action" lets a map entry call another server action (e.g. a quiet variant of SaveAnswers)
                let action = this.apiMap[controller][route].action ?? route;
                let url = controller[0].toUpperCase() + controller.slice(1) + "/" + action[0].toUpperCase() + action.slice(1);
                let reqType = this.apiMap[controller][route].type;
                let nestedParams = reqType == 'get' || reqType == 'delete';

                let loadID = Math.floor(Math.random() * 1000000);
                if (this.apiMap[controller][route].showLoader ?? true)
                    baseVue.$loader.show(loadID);

                response = await this.http[reqType](url, nestedParams ? { params: params } : params);
                var data;
                let success = this.apiMap[controller][route].success;
                let error = this.apiMap[controller][route].error;
                let successMsg;
                let errorMsg;
                let redirect = null;
                switch (response.status) {
                    case CODE_OK:
                        data = response.data === "" ? true : response.data;
                        if (success) {
                            successMsg = success[CODE_OK] ?? success.default;
                            if (success.redirect)
                                redirect = success.redirect;
                        }
                        break;
                    case CODE_CREATED:
                        data = response.data === "" ? true : response.data;
                        if (success) {
                            successMsg = success[CODE_CREATED] ?? success.default;
                            if (success.redirect)
                                redirect = success.redirect;
                        }
                        break;
                    case CODE_NO_CONTENT:
                        data = true;
                        if (success) {
                            successMsg = success[CODE_NO_CONTENT] ?? success.default;
                            if (success.redirect)
                                redirect = success.redirect;
                        }
                        break;
                    case CODE_BAD_REQUEST:
                        data = null;
                        if (error) {
                            errorMsg = error[CODE_BAD_REQUEST] ?? error.default;
                            if (error.redirect)
                                redirect = error.redirect;
                        }
                        break;
                    case CODE_FORBIDDEN:
                        data = null;
                        if (error) {
                            errorMsg = error[CODE_FORBIDDEN] ?? response.statusText ?? error.default;
                            if (error.redirect)
                                redirect = error.redirect;
                        }
                        break;
                    case CODE_NOT_FOUND:
                        data = null;
                        if (error) {
                            errorMsg = error[CODE_NOT_FOUND] ?? response.statusText ?? error.default;
                            if (error.redirect)
                                redirect = error.redirect;
                        }
                        break;
                    case CODE_INTERNAL_SERVER_ERROR:
                        data = null;
                        if (error) {
                            errorMsg = error[CODE_INTERNAL_SERVER_ERROR] ?? error.default;
                            if (error.redirect)
                                redirect = error.redirect;
                        }
                        break;
                    default:
                        data = response;
                }

                if (this.apiMap[controller][route].showLoader ?? true)
                    baseVue.$loader.close(loadID);

                if (successMsg)
                    baseVue.$message({ text: successMsg, type: "success" });
                if (errorMsg)
                    baseVue.$message({ text: errorMsg, type: "error" });
                if (redirect && redirect != baseVue.$route.path)
                    baseVue.$router.push(redirect);

                return data;
            },
            apiMap: {
                ...ACCOUNT_ROUTES,
                ...PATIENT_ROUTES,
                ...PROVIDER_ROUTES,
                ...DIAGNOSTIC_ROUTES,
                ...APPOINTMENT_ROUTES,
                ...FILE_ROUTES,
                ...REPORT_ROUTES,
                ...AI_ASSISTANT_ROUTES, // ADD THIS LINE,
                ...SCHEDULER_ROUTES,
                ...PATIENT_BILLING_ROUTES
            },
        }

        const handler = {
            has(target, controller) {
                return controller in Reflect.get(target, "apiMap");
            },
            // This defines the virtual controller
            get(target, controller) {
                const apiMap = Reflect.get(target, "apiMap");
                if (!(controller in apiMap))
                    return;
                // The target is api itself, controller is the name of the controller
                return new Proxy(baseAPI, {
                    has(target, endpoint) {
                        return endpoint in Reflect.get(target, "apiMap")[controller];
                    },
                    // The target is the controller
                    get(target, endpoint) {
                        if (!(endpoint in apiMap[controller]))
                            return;
                        return function () {
                            // FormData (file uploads) must be passed as-is; spreading it gives {}
                            const params = arguments[0] instanceof FormData ? arguments[0] : { ...(arguments[0]) };
                            return Reflect.get(target, "load").call(target, controller, endpoint, params);
                        };
                    },
                    set() { },
                });
            },
            set() { },
        };

        // Your existing API setup
        Vue.prototype.$api = new Proxy(baseAPI, handler);

        
        // Add aiHelpers as a separate Vue property
        Vue.prototype.$aiHelpers = aiHelpers;
        // ==============================================
    }
}

export default MHI_API;